import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// Third-parties.
import * as dayjs from 'dayjs';

import { Column, usePagination, useSortBy, useTable } from 'react-table';

import { CSVLink } from 'react-csv';

// Helper.
import Helper from '../../../helpers/Helper';
import { getDefaultPageSize, getToolTipPosition } from "../../../helpers/utils";

// Types.
import Alert from "../../Alert";
import InstanceTarget from '../../../types/instance/InstanceTarget';
import Wait from "../../../types/instance/Wait";
import StatementsDataTable from '../../../types/instance/tables/StatementsDataTable';

// Components.
import StatementBreakdown from '../StatementBreakdown';
import NoData from '../../NoData';
import TablePagination from '../../TablePagination';
import StatementToolTip from "./StatementToolTip";

// Constants.
import {
    DATA_LOADING,
    INSTANCE_TYPE_ORACLE,
    INSTANCE_TYPE_POSTGRES,
} from "../../Constants";
import { INSTANCE_TYPE_SQLSERVER } from "../../Constants";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { FilterContext } from "../../../context/FilterContext";
import { getFilterParameters } from "../../../helpers/utils";
import SearchInput from '../../SearchInput';

function StatementsTable(props: {
    loading?: number,
    instance: InstanceTarget,
    showByDefault: boolean,
    batchId?: string,
    batchStatementId?: string,
    instanceTime: number,
    waits: Wait[],
    statementsDataTable: StatementsDataTable[],
    currentTab?: string,
    onStatementClick?: (statementId: string, statementType?: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch') => void,
    tabIdPrefix?: string
}) {
    const tabIdPrefix = props.tabIdPrefix || '';
    const [visibleToolTip, setVisibleToolTip] = useState({
        id: '',
        position: 0
    });
    const timeRangeContext = useContext(TimeRangeContext);
    const { currentFilterOptions } = useContext(FilterContext);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(tabIdPrefix ? "" : (query.get("filter") || ""));
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    const inputRef = useRef<HTMLInputElement>(null);

    // Get filter parameters from context
    const filterParameters = getFilterParameters(currentFilterOptions);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    useEffect(() => {
        if (props.currentTab) {
            setTableFilter('')
        }
    }, [props.currentTab]);

    const data = useMemo(() => props.statementsDataTable.filter(row => {
        return (
            (row.sqlhash === null ? '' : row.sqlhash).toLowerCase().includes(tableFilter.toLowerCase()) ||
            (row.sqltext === null ? '' : row.sqltext).toLowerCase().includes(tableFilter.toLowerCase()) ||
            (row.statement.sqlid === null ? '' : row.statement.sqlid).toLowerCase().includes(tableFilter.toLowerCase())
        )
    }), [props.statementsDataTable, tableFilter]);


    const columns: Column[] = useMemo(() => [
        {
            accessor: 'statement',
            disableSortBy: true
        },
        {
            accessor: 'color',
            disableSortBy: true
        },
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            Header: '#',
            accessor: 'sqlhash',
            className: 'concatenate-small col-width-10',
            defaultCanSort: true,
            sortType: (rowA, rowB, columnId) => {
                const a = Number(rowA.values[columnId]);
                const b = Number(rowB.values[columnId]);
                return a - b;
            },
            Cell: ({cell}) => {
                // const hideTab = (props.instance.type === INSTANCE_TYPE_ORACLE || props.instance.type === INSTANCE_TYPE_DB2) ?  '' : '?hide=statistics'
                return (<React.Fragment>
                        <span className="me-1 ps-1" style={{backgroundColor: cell.row.values.color}}>&nbsp;</span>

                        {props.batchId !== undefined && !props.batchStatementId ? (
                            <Link
                                to={`/instances/${props.instance.id}/activity/batch/${props.batchId}/statement/${cell.row.values.sqlhash}/text?${timeRangeContext.getTimeRangeRedirectString()}`}>
                                {!tabIdPrefix && <i className="fal fa-arrow-up-right-from-square fa-fw" data-tip="Open in new page"/>} {cell.row.values.sqlhash}
                            </Link>
                        ) : (
                            <Link
                                to={`/instances/${props.instance.id}/activity/statement/${cell.row.values.sqlhash}/text?${timeRangeContext.getTimeRangeRedirectString()}`}>
                                {!tabIdPrefix && <i className="fal fa-arrow-up-right-from-square fa-fw" data-tip="Open in new page"/>} {cell.row.values.sqlhash}
                            </Link>
                        )}
                    </React.Fragment>
                )
            },
        },
        {
            Header: 'Statement',
            accessor: 'sqltext',
            className: 'concatenate',
            disableSortBy: true,
            Cell: ({cell}) => {
                const link = props.batchId !== undefined && !props.batchStatementId
                    ? `/instances/${props.instance.id}/activity/batch/${props.batchId}/statement/${cell.row.values.sqlhash}/text?${timeRangeContext.getTimeRangeRedirectString()}`
                    : `/instances/${props.instance.id}/activity/statement/${cell.row.values.sqlhash}/text?${timeRangeContext.getTimeRangeRedirectString()}`;
                
                if (!cell.row.values.sqltext) return <React.Fragment>-</React.Fragment>;
                
                // Inside panel: no icon, always link to page
                if (tabIdPrefix) {
                    return <Link to={link}>{cell.row.values.sqltext.substring(0, 200)}</Link>;
                }
                
                // Normal view: icon + click opens panel
                return (
                    <span
                        className="panel-link"
                        style={{ cursor: 'pointer' }}
                        onClick={() => props.onStatementClick?.(cell.row.values.sqlhash, 'statement')}
                    >
                        <i className="fal fa-sidebar-flip fa-fw" data-tip="Open in side panel"/>
                        {cell.row.values.sqltext.substring(0, 200)}
                    </span>
                );
            }
        },
        {
            Header: 'Breakdown',
            className: 'statement-waits col-width-10',
            disableSortBy: true,
            Cell: ({cell}) => (
                cell.row.values.time !== null ? (
                    cell.row.values.id < 10 ? (
                        <StatementBreakdown key={props.instanceTime}
                                            filterParameters={filterParameters}
                                            statement={cell.row.values.statement} waits={props.waits}
                                            instanceId={props.instance.id} instanceTime={props.instanceTime}
                                            maxWaitTime={data[0]?.waittime}
                                            batchId={props.batchId}/>
                    ) : (
                        cell.row.values.id < 20 ? (
                            <StatementBreakdown key={props.instanceTime}
                                                filterParameters={filterParameters}
                                                statement={cell.row.values.statement} waits={props.waits}
                                                instanceId={props.instance.id} instanceTime={props.instanceTime}
                                                maxWaitTime={data[0]?.waittime}
                                                batchId={props.batchId}/>
                        ) : (
                            <React.Fragment>-</React.Fragment>
                        )
                    )
                ) : (
                    <React.Fragment>-</React.Fragment>
                )
            )
        },
        {
            Header: 'Executions',
            accessor: 'executions',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({cell}) => (
                cell.row.values.executions !== null && cell.row.values.executions !== 0 ? (
                    Helper.getFormattedNumber(cell.row.values.executions)
                ) : (
                    <React.Fragment>-</React.Fragment>
                )
            )
        },
        {
            Header: 'Average Time',
            accessor: 'averagetime',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({cell}) => (
                cell.row.values.averagetime !== null && cell.row.values.executions !== null && cell.row.values.executions > 0 ? (
                    <React.Fragment>
                        {Helper.getTimeInEnglish(cell.row.values.averagetime)}
                    </React.Fragment>
                ) : (
                    <React.Fragment>-</React.Fragment>
                )
            )
        },
        {
            Header: 'Total Time',
            accessor: 'waittime',
            headerClassName: 'text-end',
            className: 'text-end col-width-5 text-nowrap',
            defaultCanSort: true,
            sortType: (rowA, rowB, columnId) => {
                const aWait = Number(rowA.values[columnId]) || 0;
                const bWait = Number(rowB.values[columnId]) || 0;

                if (aWait !== bWait) {
                    return bWait - aWait;
                }

                const aHash = Number(rowA.values['sqlhash']) || 0;
                const bHash = Number(rowB.values['sqlhash']) || 0;
                return aHash - bHash;
            },
            Cell: ({cell}) => (
                cell.row.values.waittime !== null ? (
                    Helper.getTimeInEnglish(cell.row.values.waittime)
                ) : (
                    <React.Fragment>-</React.Fragment>
                )
            )
        },
        {
            Header: 'Weight %',
            accessor: 'weight',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => {
                const { waittime } = cell.row.values;
                const percentage = (100 / props.instanceTime) * waittime;

                return (
                        <React.Fragment>
                            {props.instanceTime ? `${Helper.formatPercentage(percentage)}%` : '-'}
                        </React.Fragment>
                );
            }
        },
    ], [props.batchId, props.instance.id, props.instanceTime, filterParameters, props.waits, props.instance.type]);

    let hiddenColumns: string[] = [];

    switch (props.instance.type) {
        case INSTANCE_TYPE_SQLSERVER:
        case INSTANCE_TYPE_ORACLE:

            hiddenColumns = ['statement', 'id', 'color', 'executions', 'averagetime'];

            break;

        case INSTANCE_TYPE_POSTGRES:

            if (props.instance.use_pg_stat_statements === true) {

                // Show executions.
                hiddenColumns = ['statement', 'id', 'color'];
            } else {

                // Hide executions.
                hiddenColumns = ['statement', 'id', 'color', 'executions', 'averagetime'];
            }

            break;

        default:

            hiddenColumns = ['statement', 'id', 'color', 'executions', 'averagetime'];

            break;
    }

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: {pageIndex, pageSize},
    } = useTable({
            columns,
            data,
            initialState: {
                hiddenColumns,
                pageIndex: initialPageIndex,
                pageSize: initialPageSize,
            }
        },
        useSortBy, usePagination);

    useEffect(() => {
        // Don't update URL params when in panel mode (tabIdPrefix is set)
        if (tabIdPrefix) return;
        
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize, tabIdPrefix]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    return (
        <div id={`${tabIdPrefix}statements`}
             className={((props.showByDefault === true) ? 'tab-pane fade show active' : 'tab-pane fade')}
             role="tabpanel" aria-labelledby={`${tabIdPrefix}statements-tab`}>
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-scroll fa-fw" aria-hidden="true"/>
                    Statements
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target="#collapseStatements" aria-expanded="false"
                       aria-controls="collapseStatements"/>
                </div>
                <div id="collapseStatements" className="card-body collapse show">
                    {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 && (
                        <Alert
                            message={`To help preserve performance, we limit the total number of statement records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by their total wait time.`}
                            heading="Statement records" variant="alert-info"/>
                    )}
                    <div className="row row-cols-1 row-cols-md-2 table-search">
                        <div className="col col-md-10">

                            <CSVLink role="button"
                                     headers={[{label: 'ID', key: 'id'}, {
                                         label: 'Statement Text',
                                         key: 'sqltext'
                                     }, {label: 'Executions', key: 'executions'}, {
                                         label: 'Wait Time',
                                         key: 'waittime'
                                     }, {label: 'Average Time', key: 'averagetime'}]}
                                     data={data}
                                     download={`DBmarlin - ${dayjs.default().format('YYYY-MM-DD HH:mm')} - Statements.csv`}
                                     className="btn btn-sm btn-primary">
                                <i className="fal fa-file-export fa-fw"/><span>Export</span>
                            </CSVLink>
                        </div>
                        <div className="col col-md-2">
                            <SearchInput
                                ref={inputRef}
                                value={tableFilter}
                                onChange={(e) => setTableFilter(e.target.value)}
                                onClear={clearTableFilter}
                            />
                        </div>
                    </div>
                    <div className="table-responsive statements-table">
                        <table {...getTableProps()} className="table">
                            <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                            className={(column as any).className}>
                                            {column.render('Header')}
                                            {column.canSort ? (column.isSorted ? (column.isSortedDesc ?
                                                    <i className="fal fa-sort-amount-up-alt"/> :
                                                    <i className="fal fa-sort-amount-down-alt"/>) :
                                                <i className="fal fa-sort-amount-down-alt text-light"/>) : ''}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                            {page.map(row => {
                                prepareRow(row)
                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map((cell) => {
                                            return (
                                                <td {...cell.getCellProps()} className={(cell.column as any).className}
                                                    style={{borderLeft: (cell.column as any).color}}>

                                                    {cell.column.id === "sqltext" ?
                                                        <div className='tooltip-scroll ellipsis'
                                                             onMouseEnter={(event) => setVisibleToolTip({
                                                                 id: cell.row.values.sqlhash,
                                                                 position: getToolTipPosition(event)
                                                             })}
                                                             onMouseLeave={() => setVisibleToolTip({
                                                                 id: '',
                                                                 position: 0
                                                             })}
                                                        >
                                                            {cell.render('Cell')}
                                                            {(cell.row.values.sqlhash === visibleToolTip.id &&
                                                                <StatementToolTip instanceType={props.instance.type}
                                                                                  position={visibleToolTip.position}
                                                                                  id={cell.row.values.sqlhash}
                                                                                  onClick={() => props.onStatementClick?.(cell.row.values.sqlhash, 'statement')}
                                                                                  cell={cell}/>
                                                            )}
                                                        </div> : cell.render('Cell')}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                            </tbody>
                        </table>
                        <NoData
                            error={null}
                            loading={props.loading === DATA_LOADING}
                            length={data.length}
                        />
                        <TablePagination
                            length={data.length}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            pageOptions={pageOptions}
                            pageCount={pageCount}
                            canPreviousPage={canPreviousPage}
                            previousPage={previousPage}
                            canNextPage={canNextPage}
                            nextPage={nextPage}
                            gotoPage={gotoPage}
                            pageIndex={pageIndex}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StatementsTable;
